// sections/ColorwaySection.js
import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import { MdDragIndicator } from "react-icons/md";
import { CSS } from '@dnd-kit/utilities';

import { getSearchedColorList } from '../../../../utils/api-service/get-services';
import { toast } from 'react-toastify';
import { getFullImageUrl, isTemporaryUrl } from '../../../../helpers/upload';

/* ============================= */
/* SORTABLE IMAGE */
/* ============================= */
const SortableImage = ({ image, imgIndex, onRemove, colorwayId }) => {
    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: `${colorwayId}-${image._id}`,
        data: {
            type: 'image',
            colorwayId,
            originalId: image._id
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };
    const renderImage = (image) => {
        // If it's a temporary URL, use the URL directly
        if (image.isTemporary || isTemporaryUrl(image.url)) {
            return image.url;
        }

        // If we have a key, construct the full URL
        if (image.key) {
            return getFullImageUrl(image.key);
        }

        // If we have a URL that's not temporary, use it
        if (image.url && !isTemporaryUrl(image.url)) {
            return image.url;
        }

        return null;
    };

    const imageUrl = renderImage(image);

    return (
        <div ref={setNodeRef} style={style} className="position-relative">
            <img
                src={imageUrl}
                alt={image.alt || 'Colorway image'}
                className="img-thumbnail"
                style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover'
                }}
            />

            <button
                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                style={{ transform: 'translate(50%, -50%)' }}
                onClick={onRemove}
            >
                <i className="bi bi-x"></i> ×
            </button>

            <div
                {...listeners}
                {...attributes}
                className="text-center mt-1"
                style={{ cursor: 'grab' }}
            >
                <small className="text-muted">
                    Priority: {imgIndex + 1}
                </small>
            </div>
        </div>
    );
};

/* ============================= */
/* SORTABLE COLORWAY CARD */
/* ============================= */
const SortableColorway = ({
    colorway,
    index,
    onRemove,
    onAddImage,
    onRemoveImage
}) => {
    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: colorway._id,
        data: {
            type: 'colorway'
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div ref={setNodeRef} style={style} className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center bg-light">
                <div className="d-flex align-items-center">
                    <div
                        {...listeners}
                        {...attributes}
                        className="me-3"
                        style={{ cursor: 'grab' }}
                    >
                        <MdDragIndicator size={20} />
                    </div>

                    <div
                        style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            backgroundColor: `#${colorway.color?.code || 'ccc'}`,
                            marginRight: '12px',
                            border: '2px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    />

                    <div>
                        <h6 className="mb-0">
                            {colorway.color?.name || 'Unnamed Color'}
                        </h6>
                        <small className="text-muted">
                            Priority: {colorway.priority + 1}
                        </small>
                    </div>
                </div>

                <button
                    className="btn btn-sm btn-danger"
                    onClick={() => onRemove(index)}
                >
                    <i className="bi bi-trash"></i> Delete
                </button>
            </div>

            <div className="card-body">
                {/* Upload Images */}
                <div className="mb-3">
                    <label className="form-label">
                        Add Images to this Colorway
                    </label>
                    <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                            Array.from(e.target.files).forEach(file =>
                                onAddImage(index, file)
                            );
                            e.target.value = ''; // Reset input
                        }}
                    />
                </div>

                {/* Images Grid */}
                {colorway.images.length > 0 && (
                    <div>
                        <label className="form-label mb-2">
                            Images (Drag to reorder)
                        </label>
                        <div className="d-flex flex-wrap gap-3">
                            {colorway.images.map((image, imgIndex) => (
                                <SortableImage
                                    key={`${colorway._id}-${image._id}`}
                                    image={image}
                                    imgIndex={imgIndex}
                                    colorwayId={colorway._id}
                                    onRemove={() => onRemoveImage(index, imgIndex)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ============================= */
/* MAIN COMPONENT */
/* ============================= */
const ColorwaySection = ({ colorways, onColorwaysUpdate }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);

    /* ---------- SEARCH COLORS ---------- */
    const handleColorSearch = async (query) => {
        setSearchQuery(query);

        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        setLoadingSearch(true);
        try {
            const response = await getSearchedColorList(query);
            if (response.success) {
                const existingIds = colorways.map(cw => cw.color?._id).filter(id => id);
                setSearchResults(
                    response.data.filter(c => !existingIds.includes(c._id))
                );
            } else {
                toast.error('Failed to search colors');
            }
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Error searching colors');
        } finally {
            setLoadingSearch(false);
        }
    };

    /* ---------- ADD COLORWAY ---------- */
    const addColorway = (color) => {
        const newColorway = {
            _id: `cw-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            color,
            images: [],
            priority: colorways.length
        };

        onColorwaysUpdate([...colorways, newColorway]);
        setSearchQuery('');
        setSearchResults([]);
        toast.success(`Added ${color.name} colorway`);
    };

    /* ---------- REMOVE COLORWAY ---------- */
    const removeColorway = (index) => {
        const updated = colorways
            .filter((_, i) => i !== index)
            .map((cw, i) => ({ ...cw, priority: i }));

        onColorwaysUpdate(updated);
        toast.info('Colorway removed');
    };

    /* ---------- ADD IMAGE ---------- */
    const addImageToColorway = (colorwayIndex, file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }

        const updated = colorways.map((cw, i) => {
            if (i !== colorwayIndex) return cw;

            const newImage = {
                _id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                url: URL.createObjectURL(file),
                key: null,
                file: file,
                alt: cw.color?.name || 'Colorway image',
                priority: cw.images.length,
                isTemporary: true
            };

            return {
                ...cw,
                images: [...cw.images, newImage]
            };
        });

        onColorwaysUpdate(updated);
        toast.success('Image added');
    };

    /* ---------- REMOVE IMAGE ---------- */
    const removeImageFromColorway = (cwIndex, imgIndex) => {
        const updated = colorways.map((cw, i) => {
            if (i !== cwIndex) return cw;

            const imageToRemove = cw.images[imgIndex];
            // Clean up temporary URLs
            if (imageToRemove && isTemporaryUrl(imageToRemove.url)) {
                URL.revokeObjectURL(imageToRemove.url);
            }

            const images = cw.images
                .filter((_, idx) => idx !== imgIndex)
                .map((img, idx) => ({
                    ...img,
                    priority: idx,
                    // If this image was temporary and we're removing it, clean up
                    ...(img.isTemporary && { file: null })
                }));

            return { ...cw, images };
        });

        onColorwaysUpdate(updated);
        toast.info('Image removed');
    };

    /* ---------- DRAG END HANDLER ---------- */
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        // Handle Colorway Dragging
        if (activeData?.type === 'colorway' && (!overData || overData?.type === 'colorway')) {
            const cwIds = colorways.map(cw => cw._id);
            const oldIndex = cwIds.indexOf(active.id);
            const newIndex = cwIds.indexOf(over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reordered = arrayMove(colorways, oldIndex, newIndex)
                    .map((cw, i) => ({ ...cw, priority: i }));
                onColorwaysUpdate(reordered);
                toast.success('Colorway order updated');
            }
            return;
        }

        // Handle Image Dragging (only if both are images in the same colorway)
        if (activeData?.type === 'image' && overData?.type === 'image') {
            // Ensure images are from the same colorway
            if (activeData.colorwayId === overData.colorwayId) {
                const colorwayIndex = colorways.findIndex(cw => cw._id === activeData.colorwayId);

                if (colorwayIndex !== -1) {
                    const colorway = colorways[colorwayIndex];
                    const imgIds = colorway.images.map(img => img._id);
                    const activeImgId = activeData.originalId;
                    const overImgId = overData.originalId;

                    const oldIndex = imgIds.indexOf(activeImgId);
                    const newIndex = imgIds.indexOf(overImgId);

                    if (oldIndex !== -1 && newIndex !== -1) {
                        const updated = [...colorways];
                        const images = arrayMove(colorway.images, oldIndex, newIndex)
                            .map((img, i) => ({ ...img, priority: i }));
                        updated[colorwayIndex] = { ...colorway, images };
                        onColorwaysUpdate(updated);
                        toast.success('Image order updated');
                    }
                }
            }
        }
    };

    return (
        <div>
            <h5 className="border-bottom pb-2 mb-3">
                <i className="bi bi-palette me-2"></i>
                Colorways & Images *
            </h5>

            {/* Color Search Section */}
            <div className="mb-3">
                <label className="form-label">Search and Add Colors</label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search colors by name..."
                    value={searchQuery}
                    onChange={(e) => handleColorSearch(e.target.value)}
                />
                {loadingSearch && (
                    <div className="text-muted mt-2">
                        <small>Searching...</small>
                    </div>
                )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
                <div className="mb-3">
                    <label className="form-label">Available Colors</label>
                    <div className="d-flex gap-2 flex-wrap">
                        {searchResults.map(color => (
                            <button
                                key={color._id}
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => addColorway(color)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: `#${color.code || 'ccc'}`,
                                        display: 'inline-block'
                                    }}
                                />
                                {color.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Colorways List with Drag & Drop */}
            {colorways.length === 0 ? (
                <div className="alert alert-info">
                    No colorways added yet. Search and add colors above.
                </div>
            ) : (
                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={colorways.map(cw => cw._id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {colorways.map((colorway, index) => (
                            <SortableColorway
                                key={colorway._id}
                                colorway={colorway}
                                index={index}
                                onRemove={removeColorway}
                                onAddImage={addImageToColorway}
                                onRemoveImage={removeImageFromColorway}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

export default ColorwaySection;